import { useEffect, useState } from "react";
import { GetCategories } from "../../../Logic/EmsAPI/Events";
import { iCategory } from "../../../interfaces/iEvent";
import { getSessionValue, saveSessionValue } from "../../../utilities/localStore/calls";
import Confirm from "../ConfirmDialog";

export interface EventFormValues {
    title: string;
    start: Date;
    end: Date;
    details: string;
    categoryId: number;
}

export interface EventFormModalProps {
    initialValues: EventFormValues;
    isEdit: boolean;
    onSave: (values: EventFormValues) => void;
    onDelete?: () => void;
    onCancel: () => void;
}

function toLocalInputValue(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
        date.getMinutes(),
    )}`;
}

function EventFormModal(props: EventFormModalProps) {
    const [title, setTitle] = useState(props.initialValues.title);
    const [start, setStart] = useState(toLocalInputValue(props.initialValues.start));
    const [end, setEnd] = useState(toLocalInputValue(props.initialValues.end));
    const [details, setDetails] = useState(props.initialValues.details);
    const [categories, setCategories] = useState<iCategory[]>([]);
    useEffect(() => {
        async function fetchCategories() {
            let cat = getSessionValue<iCategory[]>("categories");
            if (cat) {
                setCategories(cat);
                return;
            }
            const response = await GetCategories();
            if (response.isOk && response.data) {
                response.data.categories.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
                saveSessionValue("categories", response.data.categories);
                setCategories(response.data.categories);
            }
        }
        fetchCategories();
    }, []);

    const [categoryId, setCategoryId] = useState(props.initialValues.categoryId);
    const startDate = new Date(start);
    const endDate = new Date(end);
    const isValid =
        title.trim().length > 0 && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate <= endDate;

    function handleConfirm() {
        if (!isValid) {
            return;
        }
        props.onSave({ title: title.trim(), start: startDate, end: endDate, details, categoryId });
    }

    return (
        <Confirm
            title={props.isEdit ? "Edit Event" : "New Event"}
            onConfirm={handleConfirm}
            onCancel={props.onCancel}
            isValid={isValid}
        >
            <div style={{ width: "400px" }}>
                <div className="row align-items-center formRowMargin">
                    <div className="col-sm-3">
                        <label>Title:</label>
                    </div>
                    <div className="col-sm-9">
                        <input
                            type="text"
                            className="g-TextBox-default"
                            value={title}
                            onChange={e => setTitle(e.currentTarget.value)}
                            autoFocus
                        />
                    </div>
                </div>
                <div className="row align-items-center formRowMargin">
                    <div className="col-sm-3">
                        <label>Category:</label>
                    </div>
                    <div className="col-sm-9">
                        <select
                            className="g-Select-default"
                            value={categoryId}
                            onChange={e => setCategoryId(Number(e.currentTarget.value))}
                        >
                            {categories.map(category => (
                                <option
                                    key={category.id}
                                    value={category.id}
                                >
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="row align-items-center formRowMargin">
                    <div className="col-sm-3">
                        <label>Start:</label>
                    </div>
                    <div className="col-sm-9">
                        <input
                            type="datetime-local"
                            className="g-TextBox-default"
                            value={start}
                            onChange={e => setStart(e.currentTarget.value)}
                        />
                    </div>
                </div>
                <div className="row align-items-center formRowMargin">
                    <div className="col-sm-3">
                        <label>End:</label>
                    </div>
                    <div className="col-sm-9">
                        <input
                            type="datetime-local"
                            className="g-TextBox-default"
                            value={end}
                            onChange={e => setEnd(e.currentTarget.value)}
                        />
                    </div>
                </div>
                <div className="row align-items-center formRowMargin">
                    <div className="col-sm-3">
                        <label>Details:</label>
                    </div>
                    <div className="col-sm-9">
                        <textarea
                            className="g-TextBox-default form-control form-control-sm"
                            rows={3}
                            value={details}
                            onChange={e => setDetails(e.currentTarget.value)}
                            style={{ height: "160px", fontSize: "9pt" }}
                        />
                    </div>
                </div>
                {props.isEdit && props.onDelete && (
                    <div className="delButton">
                        <button
                            type="button"
                            className="g-btn g-btn-alternate"
                            onClick={() => props.onDelete?.()}
                        >
                            Delete Event
                        </button>
                    </div>
                )}
            </div>
        </Confirm>
    );
}

export default EventFormModal;
